import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { primaryTabs, getActivePrimaryTab } from '@/constants/primary-navigation';
import type { PrimaryTab } from '@/constants/primary-navigation';

interface BottomTabBarProps {
  companionOpen: boolean;
  onCompanionToggle: () => void;
  nudgeBadgeCount?: number;
}

const TabItem: React.FC<{
  tab: PrimaryTab;
  isActive: boolean;
  onClick: () => void;
  badgeCount?: number;
}> = ({ tab, isActive, onClick, badgeCount }) => {
  const Icon = tab.icon;

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center justify-center flex-1 py-2 gap-0.5',
        'transition-colors duration-200 min-w-0',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
        isActive
          ? 'text-primary-600 dark:text-primary-400'
          : 'text-gray-400 dark:text-gray-500',
      )}
      aria-label={tab.label}
      aria-current={isActive ? 'page' : undefined}
    >
      {/* Top indicator line */}
      <div
        className={cn(
          'absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-8 rounded-b-full transition-all duration-200',
          isActive ? 'bg-primary-600 dark:bg-primary-400' : 'bg-transparent',
        )}
      />

      <div className="relative">
        <Icon className={cn('h-5 w-5', isActive && 'scale-110')} />
        {badgeCount != null && badgeCount > 0 && (
          <span className="absolute -top-1 -right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </div>

      <span className="text-[10px] font-medium leading-tight truncate">{tab.label}</span>
    </button>
  );
};

const BottomTabBar: React.FC<BottomTabBarProps> = ({
  companionOpen,
  onCompanionToggle,
  nudgeBadgeCount,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = getActivePrimaryTab(location.pathname);

  const handleTabClick = (tab: PrimaryTab) => {
    if (tab.isCompanion) {
      onCompanionToggle();
    } else if (tab.path) {
      navigate(tab.path);
    }
  };

  return (
    <nav
      className={cn(
        'lg:hidden fixed bottom-0 left-0 right-0 z-40',
        'bg-white/95 dark:bg-gray-950/95 backdrop-blur-lg',
        'border-t border-gray-200 dark:border-gray-800',
        'safe-area-bottom',
      )}
      aria-label="Primary navigation"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-stretch h-14">
        {primaryTabs.map((tab) => (
          <TabItem
            key={tab.id}
            tab={tab}
            isActive={tab.isCompanion ? companionOpen : activeTab === tab.id}
            onClick={() => handleTabClick(tab)}
            badgeCount={tab.isCompanion ? nudgeBadgeCount : undefined}
          />
        ))}
      </div>
    </nav>
  );
};

export default BottomTabBar;
